import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Paper,
  Alert,
  alpha,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  Receipt,
  Inventory,
  AttachMoney,
  Refresh,
  Visibility,
  ContactMail,
  Assignment,
  LocalShipping,
  ReceiptLong,
  PriceCheck,
  PersonAdd,
  Storefront,
  AddShoppingCart,
  RequestQuote,
  PointOfSale,
  AssignmentReturn,
  ArrowUpward,
  ArrowDownward,
  WarningAmber,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import api from '../services/api';
import PeriodSelector from '../components/common/PeriodSelector';
import { computePresetRange, DEFAULT_DATE_RANGE_PRESET } from '../utils/dateRangePresets';
import { usePermissions } from '../hooks/usePermissions';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DashboardMetrics {
  sales: { totalRevenue: number; totalOrders: number; pendingOrders: number; revenueGrowth: number };
  purchase: { totalPurchase: number; totalBills: number; pendingBills: number; overduePayments: number };
  leads: { totalLeads: number; newLeads: number; convertedLeads: number; conversionRate: number };
  inventory: { totalProducts: number; totalStockValue: number; lowStockItems: number; outOfStockItems: number };
}

interface TrendPoint { month: string; revenue: number; purchase: number; leads: number }

interface RecentOrder {
  id: string; order_number: string; customer_name: string;
  total_amount: number; status: string; created_at: string;
}

interface RecentLead {
  id: string; name: string; company: string;
  phone: string; status: string; source: string; created_at: string;
}

// ─── Formatters ───────────────────────────────────────────────────────────────

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const formatShort = (v: number) => {
  if (v >= 10_00_000) return `₹${(v / 10_00_000).toFixed(1)}L`;
  if (v >= 1_000) return `₹${(v / 1_000).toFixed(0)}K`;
  return `₹${v}`;
};

// ─── Metric Card ──────────────────────────────────────────────────────────────

interface MetricCardProps {
  title: string; value: string | number; sub: string;
  icon: React.ReactNode; color: string; growth?: number;
  onClick?: () => void; sparkData?: number[];
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, sub, icon, color, growth, onClick, sparkData }) => {
  const upTrend = growth !== undefined && growth >= 0;

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%', cursor: onClick ? 'pointer' : 'default',
        border: `1px solid ${alpha(color, 0.15)}`,
        boxShadow: `0 2px 16px ${alpha(color, 0.08)}`,
        transition: 'all 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${alpha(color, 0.18)}` } : {},
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Decorative gradient blob */}
      <Box sx={{
        position: 'absolute', top: -20, right: -20, width: 100, height: 100,
        borderRadius: '50%', background: alpha(color, 0.08),
      }} />
      <CardContent sx={{ pb: '16px !important' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.8}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} mt={0.5} color="text.primary">
              {value}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 44, height: 44 }}>
            {icon}
          </Avatar>
        </Box>

        {/* Mini Sparkline */}
        {sparkData && sparkData.length > 1 && (
          <Box sx={{ height: 40, mx: -1, mb: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData.map((v, i) => ({ v, i }))}>
                <defs>
                  <linearGradient id={`spark-${title}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2}
                  fill={`url(#spark-${title})`} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">{sub}</Typography>
          {growth !== undefined && (
            <Box display="flex" alignItems="center" gap={0.3}>
              {upTrend ? <ArrowUpward sx={{ fontSize: 14, color: 'success.main' }} />
                : <ArrowDownward sx={{ fontSize: 14, color: 'error.main' }} />}
              <Typography variant="caption" fontWeight={600}
                color={upTrend ? 'success.main' : 'error.main'}>
                {Math.abs(growth).toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Status helpers ───────────────────────────────────────────────────────────

const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const map: Record<string, any> = {
    draft: 'default', pending: 'warning', new: 'info', contacted: 'primary',
    qualified: 'success', converted: 'success', lost: 'error',
    confirmed: 'info', processing: 'primary', shipped: 'secondary',
    delivered: 'success', cancelled: 'error',
  };
  return map[status?.toLowerCase()] || 'default';
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const MainDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission, hasAnyPermission, hasRole, user } = usePermissions();

  // ─── Role buckets ──────────────────────────────────────────────────────────
  // Owner/Super Admin sees everything (today's original behavior). Sales/Purchase
  // Administrator get a scoped subset. IT Administrator has no business-data
  // permissions at all — redirect to its own dedicated dashboard rather than
  // rendering an empty/403'd business-metrics page. Every other role (Business,
  // Billing, Dispatch, Viewer) falls through to a permission-filtered view of
  // this same page — no bespoke 5th layout.
  const isOwner = Boolean(user?.is_superuser) || hasRole('Super Administrator');
  const isSalesOnly = !isOwner && hasRole('Sales Administrator');
  const isPurchaseOnly = !isOwner && hasRole('Purchase Administrator');
  const isITOnly = !isOwner && hasRole('IT Administrator');

  const canSeeSales = isOwner || isSalesOnly || (!isPurchaseOnly && hasPermission('dashboard.read') && hasAnyPermission(['reports.sales', 'sales_orders.read']));
  const canSeePurchase = isOwner || isPurchaseOnly || (!isSalesOnly && hasPermission('dashboard.read') && hasPermission('purchase.read'));
  const canSeeLeads = isOwner || isSalesOnly || (!isPurchaseOnly && hasPermission('leads.read'));
  const canSeeInventory = isOwner || (!isSalesOnly && !isPurchaseOnly && hasPermission('inventory.read'));
  const canSeeSalesActions = isOwner || isSalesOnly || (!isPurchaseOnly && hasAnyPermission(['sales_orders.create', 'estimates.create', 'invoices.create']));
  const canSeePurchaseActions = isOwner || isPurchaseOnly || (!isSalesOnly && hasAnyPermission(['purchase_orders.create', 'purchase_bills.create']));
  const canSeeAccountsActions = isOwner || (!isSalesOnly && !isPurchaseOnly && hasAnyPermission(['payments.process', 'customers.read', 'purchase_returns.read']));

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [recentOrdersMode, setRecentOrdersMode] = useState<'sales' | 'purchase'>('sales');
  const [recentLeads, setRecentLeads] = useState<RecentLead[]>([]);
  const [dateRange, setDateRange] = useState(() => computePresetRange(DEFAULT_DATE_RANGE_PRESET));

  const fetchAll = async (range: { dateFrom: string; dateTo: string } = dateRange) => {
    if (isITOnly) return; // redirected below — no business-data fetch for IT
    setLoading(true);
    setLoadError(null);

    const wantsMetrics = hasPermission('dashboard.read');
    const wantsOrders = canSeePurchase && !canSeeSales; // purchase-only recent table
    const wantsSalesOrders = !wantsOrders && hasAnyPermission(['sales_orders.read']);
    const wantsLeads = canSeeLeads;
    const dateParams = { date_from: range.dateFrom, date_to: range.dateTo };

    const [metricsResult, trendsResult, ordersResult, leadsResult] = await Promise.allSettled([
      wantsMetrics ? api.get('/admin/dashboard/metrics', { params: dateParams }) : Promise.resolve(null),
      wantsMetrics ? api.get('/admin/dashboard/trends', { params: dateParams }) : Promise.resolve(null),
      wantsOrders
        ? api.get('/admin/purchase/orders', { params: { page: 1, per_page: 5 } })
        : wantsSalesOrders
          ? api.get('/admin/sales-orders', { params: { page: 1, per_page: 5 } })
          : Promise.resolve(null),
      wantsLeads ? api.get('/admin/leads', { params: { page: 1, per_page: 5 } }) : Promise.resolve(null),
    ]);

    let anyUnexpectedFailure = false;

    if (metricsResult.status === 'fulfilled' && metricsResult.value) {
      setMetrics(metricsResult.value.data);
    } else if (metricsResult.status === 'rejected') {
      anyUnexpectedFailure = true;
      console.error('Dashboard metrics load error:', metricsResult.reason);
    }

    if (trendsResult.status === 'fulfilled' && trendsResult.value) {
      setTrends(trendsResult.value.data.trends || []);
    } else if (trendsResult.status === 'rejected') {
      anyUnexpectedFailure = true;
      console.error('Dashboard trends load error:', trendsResult.reason);
    }

    if (ordersResult.status === 'fulfilled' && ordersResult.value) {
      if (wantsOrders) {
        setRecentOrdersMode('purchase');
        const rawPOs = ordersResult.value.data.purchase_orders || ordersResult.value.data.items || [];
        setRecentOrders(rawPOs.map((o: any) => ({
          id: o.id,
          order_number: o.po_number,
          customer_name: o.supplier_name || 'Unknown',
          total_amount: o.grand_total,
          status: typeof o.status === 'object' ? o.status.value : o.status,
          created_at: o.created_at,
        })));
      } else {
        setRecentOrdersMode('sales');
        const rawOrders = ordersResult.value.data.sales_orders || ordersResult.value.data.items || [];
        setRecentOrders(rawOrders.map((o: any) => ({
          id: o.id,
          order_number: o.order_number,
          customer_name: o.customer?.name || o.customer?.billing_name || o.customer_name || 'Unknown',
          total_amount: o.total_amount,
          status: typeof o.status === 'object' ? o.status.value : o.status,
          created_at: o.created_at,
        })));
      }
    } else if (ordersResult.status === 'rejected') {
      anyUnexpectedFailure = true;
      console.error('Dashboard recent orders load error:', ordersResult.reason);
    }

    if (leadsResult.status === 'fulfilled' && leadsResult.value) {
      setRecentLeads(leadsResult.value.data.items || []);
    } else if (leadsResult.status === 'rejected') {
      anyUnexpectedFailure = true;
      console.error('Dashboard recent leads load error:', leadsResult.reason);
    }

    if (anyUnexpectedFailure) {
      setLoadError('Some dashboard data failed to load. Try refreshing — if it keeps happening, check your connection or contact an admin.');
    }

    setLoading(false);
  };

  useEffect(() => { fetchAll(dateRange); }, [dateRange]);

  if (isITOnly) {
    return <Navigate to="/admin/it/dashboard" replace />;
  }

  // ─── Quick action groups ──────────────────────────────────────────────────

  const actionGroups = [
    {
      label: 'Sales', gradient: 'linear-gradient(135deg,#1565c0,#1976d2)',
      lightBg: '#e3f2fd', borderColor: '#1976d2', visible: canSeeSalesActions,
      actions: [
        { label: 'New Estimate', icon: <RequestQuote />, path: '/admin/documents/estimates/create', permission: 'estimates.create' },
        { label: 'New Sales Order', icon: <AddShoppingCart />, path: '/admin/documents/sales-orders/create', permission: 'sales_orders.create' },
        { label: 'New Invoice', icon: <ReceiptLong />, path: '/admin/documents/invoices/create', permission: 'invoices.create' },
        { label: 'Sales Return', icon: <AssignmentReturn />, path: '/admin/documents/sales-returns/create', permission: 'sales_returns.create' },
        { label: 'Add Customer', icon: <PersonAdd />, path: '/admin/parties/customers/create', permission: 'customers.create' },
        { label: 'Add Lead', icon: <ContactMail />, path: '/admin/leads/create', permission: 'leads.write' },
      ],
    },
    {
      label: 'Purchase', gradient: 'linear-gradient(135deg,#6a1b9a,#8e24aa)',
      lightBg: '#f3e5f5', borderColor: '#8e24aa', visible: canSeePurchaseActions,
      actions: [
        { label: 'New Purchase Order', icon: <ShoppingBag />, path: '/admin/purchase/orders/create', permission: 'purchase_orders.create' },
        { label: 'New Purchase Bill', icon: <Receipt />, path: '/admin/purchase/bills/create', permission: 'purchase_bills.create' },
        { label: 'Add Supplier', icon: <Storefront />, path: '/admin/parties/suppliers/create', permission: 'suppliers.create' },
        { label: 'Add Product', icon: <Inventory />, path: '/admin/products/create', permission: 'products.create' },
        { label: 'Delivery Challan', icon: <LocalShipping />, path: '/admin/documents/delivery-challans/create', permission: 'delivery_challans.create' },
        { label: 'Stock Adjustment', icon: <Assignment />, path: '/admin/inventory/adjustments', permission: 'inventory.adjust' },
      ],
    },
    {
      label: 'Accounts', gradient: 'linear-gradient(135deg,#1b5e20,#2e7d32)',
      lightBg: '#e8f5e9', borderColor: '#2e7d32', visible: canSeeAccountsActions,
      actions: [
        { label: 'Record Payment', icon: <AttachMoney />, path: '/admin/payments/create', permission: 'payments.process' },
        { label: 'Party Ledgers', icon: <PointOfSale />, path: '/admin/parties/ledgers', permission: 'customers.read' },
        { label: 'Purchase Returns', icon: <AssignmentReturn />, path: '/admin/purchase/returns', permission: 'purchase_returns.read' },
        { label: 'Analytics', icon: <PriceCheck />, path: '/admin/sales/dashboard', permission: 'reports.sales' },
      ],
    },
  ].map((group) => ({
    ...group,
    actions: group.actions.filter((a) => isOwner || hasPermission(a.permission)),
  })).filter((group) => group.visible && group.actions.length > 0);

  // ─── Leads donut data ─────────────────────────────────────────────────────
  const leadsDonut = metrics ? [
    { name: 'Converted', value: metrics.leads.convertedLeads, color: '#2e7d32' },
    { name: 'New', value: metrics.leads.newLeads, color: '#1976d2' },
    { name: 'Other', value: Math.max(0, metrics.leads.totalLeads - metrics.leads.convertedLeads - metrics.leads.newLeads), color: '#ed6c02' },
  ].filter((d) => d.value > 0) : [];

  // ─── Inventory bar data ───────────────────────────────────────────────────
  const inventoryBar = metrics ? [
    { name: 'Total', value: metrics.inventory.totalProducts, fill: '#1976d2' },
    { name: 'Low Stock', value: metrics.inventory.lowStockItems, fill: '#ed6c02' },
    { name: 'Out of Stock', value: metrics.inventory.outOfStockItems, fill: '#d32f2f' },
  ] : [];

  // ─── Sparklines from trend data ───────────────────────────────────────────
  const revSpark = trends.map((t) => t.revenue);
  const purSpark = trends.map((t) => t.purchase);
  const leadSpark = trends.map((t) => t.leads);

  const showRevenueLine = canSeeSales;
  const showPurchaseLine = canSeePurchase;
  const chartTitle = showRevenueLine && showPurchaseLine
    ? 'Revenue vs Purchase'
    : showRevenueLine ? 'Revenue Trend' : 'Purchase Trend';

  const recentTableTitle = recentOrdersMode === 'purchase' ? 'Recent Purchase Orders' : 'Recent Orders';
  const recentTableViewAllPath = recentOrdersMode === 'purchase' ? '/admin/purchase/orders' : '/admin/documents/sales-orders';
  const recentTableRowPath = (id: string) => recentOrdersMode === 'purchase'
    ? `/admin/purchase/orders/${id}`
    : `/admin/documents/sales-orders/${id}`;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="text.primary">
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5}>
          <PeriodSelector onChange={(dateFrom, dateTo) => setDateRange({ dateFrom, dateTo })} />
          <Tooltip title="Refresh data">
            <IconButton onClick={() => fetchAll(dateRange)} color="primary"
              sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider', boxShadow: 1 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={() => fetchAll(dateRange)}>Retry</Button>
        }>
          {loadError}
        </Alert>
      )}

      {/* ── Low Stock Alert — surfaced up top since stock-out risk is one of the
          most actionable single numbers for a business owner; only shown when
          there's actually something to act on. ── */}
      {!loading && canSeeInventory && metrics && (metrics.inventory.lowStockItems > 0 || metrics.inventory.outOfStockItems > 0) && (
        <Alert
          severity={metrics.inventory.outOfStockItems > 0 ? 'error' : 'warning'}
          icon={<WarningAmber />}
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/admin/inventory')}>
              View Inventory
            </Button>
          }
        >
          {metrics.inventory.outOfStockItems > 0 && (
            <strong>{metrics.inventory.outOfStockItems} product{metrics.inventory.outOfStockItems === 1 ? '' : 's'} out of stock. </strong>
          )}
          {metrics.inventory.lowStockItems > 0 && (
            <>{metrics.inventory.lowStockItems} product{metrics.inventory.lowStockItems === 1 ? '' : 's'} running low on stock.</>
          )}
        </Alert>
      )}

      {/* ── KPI Cards ── */}
      <Grid container spacing={2.5} mb={3}>
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={160} />
            </Grid>
          ))
        ) : metrics && (
          <>
            {canSeeSales && (
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard title="Sales Revenue" value={formatCurrency(metrics.sales.totalRevenue)}
                  sub={`${metrics.sales.totalOrders} orders · ${metrics.sales.pendingOrders} pending`}
                  icon={<Receipt />} color="#1976d2" growth={metrics.sales.revenueGrowth}
                  sparkData={revSpark} onClick={() => navigate('/admin/sales/dashboard')} />
              </Grid>
            )}
            {canSeePurchase && (
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard title="Purchase Amount" value={formatCurrency(metrics.purchase.totalPurchase)}
                  sub={`${metrics.purchase.totalBills} bills · ${metrics.purchase.pendingBills} pending`}
                  icon={<ShoppingBag />} color="#8e24aa"
                  sparkData={purSpark} onClick={() => navigate('/admin/purchase/dashboard')} />
              </Grid>
            )}
            {canSeeLeads && (
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard title="Total Leads" value={metrics.leads.totalLeads}
                  sub={`${metrics.leads.conversionRate.toFixed(1)}% conversion rate`}
                  icon={<ContactMail />} color="#ed6c02"
                  sparkData={leadSpark} onClick={() => navigate('/admin/leads')} />
              </Grid>
            )}
            {canSeeInventory && (
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard title="Stock Value" value={formatCurrency(metrics.inventory.totalStockValue)}
                  sub={`${metrics.inventory.totalProducts} products · ${metrics.inventory.outOfStockItems} OOS`}
                  icon={<Inventory />} color="#0288d1"
                  onClick={() => navigate('/admin/inventory')} />
              </Grid>
            )}
          </>
        )}
      </Grid>

      {/* ── Revenue vs Purchase Chart ── */}
      {(showRevenueLine || showPurchaseLine) && (
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={canSeeLeads ? 8 : 12}>
            <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{chartTitle}</Typography>
                    <Typography variant="caption" color="text.secondary">Last 6 months comparison</Typography>
                  </Box>
                </Box>
                {loading ? <Skeleton variant="rectangular" height={220} /> : (
                  <ResponsiveContainer width="100%" height={230}>
                    <AreaChart data={trends} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradRev" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1976d2" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradPur" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8e24aa" stopOpacity={0.18} />
                          <stop offset="95%" stopColor="#8e24aa" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={formatShort} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <ReTooltip
                        formatter={(value: any, name: string) => [formatCurrency(value), name === 'revenue' ? 'Revenue' : 'Purchase']}
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}
                      />
                      <Legend formatter={(v) => v === 'revenue' ? 'Revenue' : 'Purchase'} />
                      {showRevenueLine && (
                        <Area type="monotone" dataKey="revenue" stroke="#1976d2" strokeWidth={2.5}
                          fill="url(#gradRev)" dot={{ r: 4, fill: '#1976d2' }} activeDot={{ r: 6 }} />
                      )}
                      {showPurchaseLine && (
                        <Area type="monotone" dataKey="purchase" stroke="#8e24aa" strokeWidth={2.5}
                          fill="url(#gradPur)" dot={{ r: 4, fill: '#8e24aa' }} activeDot={{ r: 6 }} />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Leads Donut */}
          {canSeeLeads && (
            <Grid item xs={12} md={4}>
              <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} mb={0.5}>Lead Status</Typography>
                  <Typography variant="caption" color="text.secondary">Breakdown by category</Typography>
                  {loading ? <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', mt: 2 }} /> : (
                    leadsDonut.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={leadsDonut} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                            paddingAngle={4} dataKey="value">
                            {leadsDonut.map((entry, index) => (
                              <Cell key={index} fill={entry.color} />
                            ))}
                          </Pie>
                          <ReTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height={220}>
                        <Typography color="text.secondary" variant="body2">No lead data yet</Typography>
                      </Box>
                    )
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* ── Leads Monthly Bar + Inventory ── */}
      {(canSeeLeads || canSeeInventory) && (
        <Grid container spacing={2.5} mb={3}>
          {canSeeLeads && (
            <Grid item xs={12} md={canSeeInventory ? 7 : 12}>
              <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} mb={0.5}>Monthly Leads</Typography>
                  <Typography variant="caption" color="text.secondary">New leads per month</Typography>
                  {loading ? <Skeleton variant="rectangular" height={200} sx={{ mt: 1 }} /> : (
                    <ResponsiveContainer width="100%" height={210}>
                      <BarChart data={trends} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <ReTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                        <Bar dataKey="leads" name="Leads" fill="#ed6c02" radius={[5, 5, 0, 0]} maxBarSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {canSeeInventory && (
            <Grid item xs={12} md={canSeeLeads ? 5 : 12}>
              <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} mb={0.5}>Inventory Health</Typography>
                  <Typography variant="caption" color="text.secondary">Products by stock status</Typography>
                  {loading ? <Skeleton variant="rectangular" height={200} sx={{ mt: 1 }} /> : (
                    <ResponsiveContainer width="100%" height={210}>
                      <BarChart data={inventoryBar} layout="vertical" margin={{ top: 10, right: 10, left: 20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <ReTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                        <Bar dataKey="value" name="Count" radius={[0, 5, 5, 0]} maxBarSize={32}>
                          {inventoryBar.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* ── Quick Actions ── */}
      {actionGroups.length > 0 && (
        <Grid container spacing={2.5} mb={3}>
          {actionGroups.map((group) => (
            <Grid item xs={12} md={Math.max(4, Math.floor(12 / actionGroups.length))} key={group.label}>
              <Paper elevation={0} sx={{ border: `1px solid ${alpha(group.borderColor, 0.2)}`, borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ background: group.gradient, px: 2.5, py: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="white" letterSpacing={0.5}>
                    {group.label}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: group.lightBg, p: 1.5 }}>
                  <Grid container spacing={1}>
                    {group.actions.map((action) => (
                      <Grid item xs={6} key={action.label}>
                        <Button fullWidth variant="contained" size="small" startIcon={action.icon}
                          onClick={() => navigate(action.path)}
                          sx={{
                            justifyContent: 'flex-start', bgcolor: 'white', color: group.borderColor,
                            border: `1px solid ${alpha(group.borderColor, 0.15)}`, boxShadow: 'none',
                            textTransform: 'none', fontWeight: 500, fontSize: '0.78rem',
                            py: 0.9, px: 1.2, borderRadius: 2,
                            '&:hover': { bgcolor: group.borderColor, color: 'white', boxShadow: `0 4px 12px ${alpha(group.borderColor, 0.4)}` },
                            '& .MuiButton-startIcon': { mr: 0.5 },
                          }}>
                          {action.label}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Recent Tables ── */}
      <Grid container spacing={2.5}>
        {/* Recent Orders (sales or purchase, depending on role) */}
        {(canSeeSales || canSeePurchase) && (
          <Grid item xs={12} md={canSeeLeads && canSeeSales ? 6 : 12}>
            <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={700}>{recentTableTitle}</Typography>
                  <Button size="small" endIcon={<Visibility />} onClick={() => navigate(recentTableViewAllPath)}>
                    View All
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                        <TableCell>{recentOrdersMode === 'purchase' ? 'PO #' : 'Order #'}</TableCell>
                        <TableCell>{recentOrdersMode === 'purchase' ? 'Supplier' : 'Customer'}</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        [0, 1, 2, 3, 4].map((i) => (
                          <TableRow key={i}>
                            <TableCell colSpan={4}><Skeleton /></TableCell>
                          </TableRow>
                        ))
                      ) : recentOrders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">No recent orders</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentOrders.map((order) => (
                          <TableRow key={order.id} hover sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(recentTableRowPath(order.id))}>
                            <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>{order.order_number}</TableCell>
                            <TableCell>{order.customer_name}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(order.total_amount)}</TableCell>
                            <TableCell>
                              <Chip label={order.status} size="small" color={getStatusColor(order.status)}
                                sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Leads — only alongside the sales view, and only for Owner (Sales-only gets the compact leads widgets above instead) */}
        {isOwner && canSeeLeads && (
          <Grid item xs={12} md={6}>
            <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={700}>Recent Leads</Typography>
                  <Button size="small" endIcon={<Visibility />} onClick={() => navigate('/admin/leads')}>
                    View All
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        [0, 1, 2, 3, 4].map((i) => (
                          <TableRow key={i}>
                            <TableCell colSpan={4}><Skeleton /></TableCell>
                          </TableRow>
                        ))
                      ) : recentLeads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">No recent leads</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentLeads.map((lead) => (
                          <TableRow key={lead.id} hover sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/admin/leads/${lead.id}`)}>
                            <TableCell sx={{ fontWeight: 600 }}>{lead.name}</TableCell>
                            <TableCell>{lead.company || '—'}</TableCell>
                            <TableCell>
                              <Chip label={lead.source} size="small" variant="outlined"
                                sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
                            </TableCell>
                            <TableCell>
                              <Chip label={lead.status} size="small" color={getStatusColor(lead.status)}
                                sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {!loading && !metrics && actionGroups.length === 0 && !canSeeSales && !canSeePurchase && !canSeeLeads && !canSeeInventory && (
        <Alert severity="info" sx={{ mt: 2 }}>
          There's nothing to show here for your role yet. Use the navigation menu to access the areas you have permission for.
        </Alert>
      )}
    </Box>
  );
};

export default MainDashboard;
